import { useId } from 'react';
import { SWOOSH_PATH } from './swoosh';

/*
  Horizontal lockup: mark plus wordmark, inlined for the same reasons as JitMark.

  The wordmark is paths, not <text>. A logo that depends on a font renders differently on
  every machine that lacks it, and this same lockup appears in an installer dialog where
  there is no web font to load.

  Clear space: at least the height of the "I" (54 units on the 128 grid) on all sides.
  Below 120px wide, use JitMark alone.
*/

export interface JitLogoProps {
  /** Rendered height in pixels. The lockup's intrinsic ratio is 281 by 128. */
  readonly height?: number | undefined;
  readonly className?: string | undefined;
  readonly label?: string;
}

export function JitLogo({
  height = 32,
  className,
  label = 'JIT',
}: JitLogoProps): React.JSX.Element {
  const uid = useId();
  const maskId = `jit-logo-cut-${uid}`;
  const gradientId = `jit-logo-swoosh-${uid}`;
  const titleId = `jit-logo-title-${uid}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 281 128"
      height={height}
      width={(281 / 128) * height}
      className={className}
      role="img"
      aria-labelledby={titleId}
    >
      <title id={titleId}>{label}</title>
      <defs>
        <linearGradient
          id={gradientId}
          x1="24"
          y1="220"
          x2="228"
          y2="60"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="var(--jit-blue-300)" />
          <stop offset="0.55" stopColor="var(--jit-blue-400)" />
          <stop offset="1" stopColor="var(--jit-blue-500)" />
        </linearGradient>
        <mask id={maskId}>
          <rect x="0" y="0" width="256" height="256" fill="#fff" />
          <g
            transform="rotate(-13 162 94)"
            fill="#000"
            stroke="#000"
            strokeWidth="14"
            strokeLinejoin="round"
          >
            <rect x="104" y="46" width="116" height="86" rx="13" />
            <rect x="153" y="132" width="18" height="13" />
            <rect x="132" y="143" width="60" height="12" rx="6" />
          </g>
        </mask>
      </defs>

      <g transform="translate(4 8) scale(0.4375)">
        <g mask={`url(#${maskId})`}>
          <path d={SWOOSH_PATH} fill={`url(#${gradientId})`} />
        </g>
        <g transform="rotate(-13 162 94)">
          <rect x="104" y="46" width="116" height="86" rx="13" fill="var(--brand-bezel)" />
          <rect x="115" y="57" width="94" height="58" rx="5" fill="var(--brand-screen)" />
          <rect x="153" y="132" width="18" height="13" fill="var(--brand-bezel)" />
          <rect x="132" y="143" width="60" height="12" rx="6" fill="var(--brand-bezel)" />
        </g>
      </g>

      <g transform="translate(132 0)" fill="var(--brand-word)">
        <path d="M 25 37 H 39 V 71 C 39 82, 30 91, 19 91 H 6 V 77 H 19 C 22 77, 25 74, 25 71 Z" />
        <path d="M 55 37 H 69 V 91 H 55 Z" />
        <path d="M 85 37 H 141 V 51 H 120 V 91 H 106 V 51 H 85 Z" />
      </g>
    </svg>
  );
}
